import { ChangeDetectorRef, Component, ComponentRef, ElementRef, EventEmitter,
         ViewChild
       } from '@angular/core';
import {  MatDialog, MatIconRegistry } from '@angular/material';
import { DomSanitizer } from '@angular/platform-browser';

import { Button } from 'app/dialogs/dialog-buttons.component';
import { DeletePlaceholderDialogComponent
       } from 'app/dialogs/delete-placeholder-dialog/delete-placeholder-dialog.component'; // tslint:disable-line
import { ToolboxService } from 'app/toolboxes/toolbox.service';
import { StageConstants } from 'core/constants';
import { ProductService } from 'core/product/product.service';
import { EventTrackingService } from 'core/tracking/event-tracking.service';
import { FittingSidePosition, Utils } from 'core/utils';
import { Placeholder, PlaceholderType } from '../../placeholder';
import { TextPlaceholder } from '../../text-placeholder';
import { OverlayComponent, Trigger, TriggerGroup } from '../overlay.component';
import { Action, actionBarIcons } from './action-bar.actions';

interface Vector {
  x: number;
  y: number;
}

interface Gravity {
  origin: Vector;
  delta: Vector;
}

export interface PositioningHints
{
  marginRight?: number;
  gravity?: Gravity;
}

const IMAGE_TRIGGERS: Array<Trigger> = [
  {
    icon: actionBarIcons[Action.CROP_IMAGE],
    action: Action.CROP_IMAGE
  },
  {
    icon: actionBarIcons[Action.CLEAR_IMAGE],
    action: Action.CLEAR_IMAGE
  },
];

const SMALL_DEVICE_IMAGE_TRIGGERS: Array<Trigger> = [
  {
    icon: actionBarIcons[Action.OPEN_GALLERY],
    action: Action.OPEN_GALLERY
  },
];

const TEXT_TRIGGERS: Array<Trigger> = [
  {
    icon: actionBarIcons[Action.EDIT_TEXT],
    action: Action.EDIT_TEXT
  },
];

const FLEXIBLE_TRIGGERS: Array<Trigger> = [
  {
    icon: actionBarIcons[Action.DELETE],
    action: Action.DELETE
  },
];

// export is required for tests!
export { IMAGE_TRIGGERS, SMALL_DEVICE_IMAGE_TRIGGERS, TEXT_TRIGGERS,
         FLEXIBLE_TRIGGERS };

export interface ActionEvent
{
  action: Action;
  placeholder: Placeholder;
}

@Component({
  selector: 'action-bar',
  templateUrl: './action-bar.component.html',
  styleUrls: [ './action-bar.component.scss' ]
})
export class ActionBarComponent extends OverlayComponent
{
  private static _AVAILABLE_SIDES: Array<FittingSidePosition> = [
    FittingSidePosition.Bottom,
    FittingSidePosition.Left,
    FittingSidePosition.Top,
    FittingSidePosition.Right,
    FittingSidePosition.CenterHorizontal,
    FittingSidePosition.CenterVertical,
    FittingSidePosition.Gravity1,
    FittingSidePosition.Gravity2,
    FittingSidePosition.Gravity3,
    FittingSidePosition.Gravity4,
    FittingSidePosition.Gravity5,
    FittingSidePosition.Gravity6,
    FittingSidePosition.Gravity7,
    FittingSidePosition.Gravity8,
  ];

  private static _FALLBACK_SIDE: FittingSidePosition =
    FittingSidePosition.CenterHorizontal;

  public actionTriggered: EventEmitter<ActionEvent> =
    new EventEmitter<ActionEvent>();
  public trackByLabel: Object = Utils.trackByLabel;

  public componentRef: ComponentRef<ActionBarComponent>;
  public display: boolean = true;

  private positioningHints: PositioningHints = {};

  @ViewChild('triggersRef') protected triggersRef: ElementRef;

  constructor(
      element: ElementRef,
      cdr: ChangeDetectorRef,
      mir: MatIconRegistry,
      sanitizer: DomSanitizer,
      private toolboxService: ToolboxService,
      private dialog: MatDialog,
      private eventTrackingService: EventTrackingService,
      private productService: ProductService
  ) {
    super(element, cdr, mir, sanitizer);
  }

  protected get AVAILABLE_SIDES(): Array<FittingSidePosition>
  {
    return ActionBarComponent._AVAILABLE_SIDES;
  }

  protected get FALLBACK_SIDE(): FittingSidePosition
  {
    return ActionBarComponent._FALLBACK_SIDE;
  }

  public initialize(
    placeholder: Placeholder,
    positioningHints?: PositioningHints): void
  {
    this.positioningHints = {
      gravity: this.getGravity(placeholder),
      ...(positioningHints || this.positioningHints),
    };
    this.nativeElement.addEventListener(
        'mouseleave', this.mouseLeave.bind(this));

    super.initialize(placeholder);
  }

  public async startActionIfPossible(): Promise<void>
  {
    const triggers = this.getTriggers(this.placeholder);
    if (triggers.length === 1) {
      this.display = false;
      await this.triggerClicked(triggers[0].action);
    }
    else {
      this.display = true;
    }
  }

  private getGravity(placeholder: Placeholder): Gravity
  {
    const bbox = e => e.node.getBoundingClientRect();

    const center = r => ({
      x: (r.x + r.width / 2),
      y: (r.y + r.height / 2)
    });

    const wrapper = <any>placeholder.wrapper;
    const sceneCenter = center(bbox(wrapper.doc()));
    const subjectDimensions = bbox(wrapper);
    const subjectCenter = center(subjectDimensions);

    return {
      origin: {
        x: sceneCenter.x - subjectCenter.x,
        y: sceneCenter.y - subjectCenter.y
      },
      delta: {
        x: subjectDimensions.width / 2,
        y: subjectDimensions.height / 2,
      }
    };
  }

  protected checkTriggersAreUseable(): boolean
  {
    if (! (this.triggersRef && this.placeholder)) {
      return false;
    }

    const viewPort = ((<any>this.placeholder.wrapper)
                      .doc()
                      .node
                      .getBoundingClientRect());

    const hitBox = (this.triggersRef
                    .nativeElement
                    .getBoundingClientRect());

    const hitBoxIsContained = (hitBox.left > viewPort.left &&
                               hitBox.right < viewPort.right &&
                               hitBox.top > viewPort.top &&
                               hitBox.bottom < viewPort.bottom);

    return hitBoxIsContained;
  }

  protected updateTriggerGroup(): void
  {
    const positioningMethod = (() => {
      switch (this.triggerSide) {
        case FittingSidePosition.CenterHorizontal:
          return positionCenterHorizontal;
        case FittingSidePosition.CenterVertical:
          return positionCenterVertical;
        case FittingSidePosition.Top:
          return positionTop;
        case FittingSidePosition.Left:
          return positionLeft;
        case FittingSidePosition.Bottom:
          return positionBottom;
        case FittingSidePosition.Right:
          return positionRight;
        case FittingSidePosition.Gravity1:
          return positionGravity(1 / 8);
        case FittingSidePosition.Gravity2:
          return positionGravity(2 / 8);
        case FittingSidePosition.Gravity3:
          return positionGravity(3 / 8);
        case FittingSidePosition.Gravity4:
          return positionGravity(4 / 8);
        case FittingSidePosition.Gravity5:
          return positionGravity(5 / 8);
        case FittingSidePosition.Gravity6:
          return positionGravity(6 / 8);
        case FittingSidePosition.Gravity7:
          return positionGravity(7 / 8);
        case FittingSidePosition.Gravity8:
          return positionGravity(8 / 8);
        default:
          return positionBottom;
      }
    })();

    this.triggerGroup = positioningMethod(
      this.getTriggers(this.placeholder), this.positioningHints);
  }

  private mouseLeave(event: MouseEvent): void
  {
    this.placeholder.mouseLeave(event);
  }

  public getTriggers(placeholder: Placeholder): Array<Trigger>
  {
    let triggers = undefined;
    if (placeholder.type === PlaceholderType.Image) {
      triggers = (Utils.isSmallDevice() || Utils.isMediumDevice())
          ? IMAGE_TRIGGERS.concat(SMALL_DEVICE_IMAGE_TRIGGERS)
          : IMAGE_TRIGGERS;

      // remove CROP_IMAGE if image has zoom-mode="fit"
      // cause it does not work as intended
      if (placeholder.zoomMode === StageConstants.ZOOM_MODE_FIT) {
        triggers = triggers.filter((t) => t.action !== Action.CROP_IMAGE);
      }
    }
    else if (placeholder.type === PlaceholderType.Text) {
      triggers = TEXT_TRIGGERS;
    }

    if (placeholder.isFlexible()) {
      triggers = triggers.concat(FLEXIBLE_TRIGGERS);
    }
    this.loadIconsIfRequired(triggers);
    return triggers;
  }

  public async triggerClicked(action: string): Promise<void>
  {
    switch (action) {
      case Action.CLEAR_IMAGE:
        await this.placeholder.reset();
        break;

      case Action.CROP_IMAGE:
        this.toolboxService.edit(this.placeholder);
        break;

      case Action.OPEN_GALLERY:
        this.toolboxService.edit(this.placeholder, 'GalleryToolbox');
        break;

      case Action.EDIT_TEXT:
        (<TextPlaceholder>this.placeholder).doubleClick();
        break;

      case Action.DELETE:
        if (!this.placeholder.isUnchanged()) {
          this.dialog.open(DeletePlaceholderDialogComponent, {
            data: {
              buttons: {
                primary: Button.IGNORE_AND_CONTINUE,
                secondary: Button.CANCEL
              }
            }
          }).afterClosed().subscribe(result => {
              if (result === Button.Choice.IGNORE) {
                this.placeholder.remove();
                this.eventTrackingService.track({
                  category: this.productService.getCurrentProduct().id,
                  action: 'delete_text',
                  label: 'continue anyway'
                });
              }
              else {
                this.eventTrackingService.track({
                  category: this.productService.getCurrentProduct().id,
                  action: 'delete_text',
                  label: 'cancel'
                });
              }
            });
        }
        else {
          this.placeholder.remove();
        }
      break;

      default: break;
    }
  }

  public destroy(): void
  {
    this.nativeElement.removeEventListener('mouseleave', this.mouseLeave);
    this.componentRef.destroy();
  }
}

function positionGravity(scale: number):
    (triggers: Array<Trigger>, hints: PositioningHints) => TriggerGroup
{
  return (triggers, hints) => ({
    triggers: triggers,
    top: `${hints.gravity.origin.y * scale + hints.gravity.delta.y}px`,
    left: `${hints.gravity.origin.x * scale + hints.gravity.delta.x}px`,
    alignHorizontal: '-50%',
    alignVertical: '-50%',
    direction: 'row',
  });
}

function positionCenterHorizontal(
  triggers: Array<Trigger>, hints: PositioningHints): TriggerGroup
{
  return {
    triggers: triggers,
    top: '50%',
    left: '50%',
    alignHorizontal: '-50%',
    alignVertical: '-50%',
    direction: 'row',
  };
}

function positionCenterVertical(
  triggers: Array<Trigger>, hints: PositioningHints): TriggerGroup
{
  return {
    triggers: triggers,
    top: '50%',
    left: '50%',
    alignHorizontal: '-50%',
    alignVertical: '-50%',
    direction: 'column',
  };
}

function positionTop(
  triggers: Array<Trigger>, hints: PositioningHints): TriggerGroup
{
  return {
    triggers: triggers,
    top: '-10px',
    left: '50%',
    alignHorizontal: '-50%',
    alignVertical: '-100%',
    direction: 'row',
  };
}

function positionBottom(
  triggers: Array<Trigger>, hints: PositioningHints): TriggerGroup
{
  return {
    triggers: triggers,
    top: '100%',
    left: '50%',
    alignHorizontal: '-50%',
    alignVertical: '10px',
    direction: 'row',
  };
}

function positionLeft(
  triggers: Array<Trigger>, hints: PositioningHints): TriggerGroup
{
  return {
    triggers: triggers,
    top: '50%',
    left: '-10px',
    alignHorizontal: '-100%',
    alignVertical: '-50%',
    direction: 'column',
  };
}

function positionRight(
  triggers: Array<Trigger>, hints: PositioningHints): TriggerGroup
{
  return {
    triggers: triggers,
    top: '50%',
    left: '100%',
    alignHorizontal: `${10 + (hints.marginRight || 0)}px`,
    alignVertical: '-50%',
    direction: 'column',
  };
}



// WEBPACK FOOTER //
// ./src/app/collage-designer/stage/placeholder/overlays/action-bar/action-bar.component.ts